import { Evolutivo } from '../types';
import { Database, dbToEvolutivo, evolutivoToDb } from './supabaseClient';
import { fetchAll, fetchById, createRecord, updateRecord, deleteRecord, getCurrentUser } from './baseService';
import { supabase, isSupabaseAvailable } from './supabaseClient';

// ============================================
// EVOLUTIVO SERVICE
// ============================================

type EvolutivoRow = Database['public']['Tables']['evolutivos']['Row'];

/**
 * Get all evolutivos for the current user
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getEvolutivos = async (): Promise<Evolutivo[]> => {
  return fetchAll<EvolutivoRow, Evolutivo>(
    'evolutivos',
    dbToEvolutivo,
    {
      orderBy: 'created_at',
      ascending: false
    }
  );
};

/**
 * Get evolutivos for a specific patient
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getEvolutivosByPatientId = async (patientId: string): Promise<Evolutivo[]> => {
  return fetchAll<EvolutivoRow, Evolutivo>(
    'evolutivos',
    dbToEvolutivo,
    {
      orderBy: 'date',
      ascending: false,
      filters: [{ column: 'patient_id', value: patientId }]
    }
  );
};

/**
 * Get a single evolutivo by ID
 */
export const getEvolutivoById = async (id: string): Promise<Evolutivo | null> => {
  return fetchById<EvolutivoRow, Evolutivo>('evolutivos', id, dbToEvolutivo, { skipUserFilter: true });
};

/**
 * Create a new evolutivo
 * SECURITY: Explicitly sets user_id from authenticated user
 */
export const createEvolutivo = async (
  evolutivoData: Omit<Evolutivo, 'id' | 'createdAt' | 'updatedAt' | 'userId'>
): Promise<Evolutivo | null> => {
  return createRecord<EvolutivoRow, Evolutivo, Omit<Evolutivo, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>(
    'evolutivos',
    evolutivoData,
    evolutivoToDb,
    dbToEvolutivo
  );
};

/**
 * Update an existing evolutivo
 */
export const updateEvolutivo = async (
  id: string,
  updates: Partial<Omit<Evolutivo, 'id' | 'createdAt' | 'updatedAt' | 'userId' | 'patientId'>>
): Promise<Evolutivo | null> => {
  const dbUpdates: Record<string, unknown> = {};

  if (updates.date !== undefined) dbUpdates.date = updates.date;
  if (updates.content !== undefined) dbUpdates.content = updates.content;
  if (updates.linkedTestResultId !== undefined) dbUpdates.linked_test_result_id = updates.linkedTestResultId || null;
  if (updates.analysisInstructions !== undefined) dbUpdates.analysis_instructions = updates.analysisInstructions || null;
  if (updates.aiAnalysis !== undefined) dbUpdates.ai_analysis = updates.aiAnalysis || null;

  return updateRecord<EvolutivoRow, Evolutivo>('evolutivos', id, dbUpdates, dbToEvolutivo, { skipUserFilter: true });
};

/**
 * Delete an evolutivo
 */
export const deleteEvolutivo = async (id: string): Promise<boolean> => {
  return deleteRecord('evolutivos', id, { skipUserFilter: true });
};

/**
 * Increment evolutivo analysis usage counter for the current user
 * This should be called AFTER generating an AI analysis to track usage limits
 */
export const incrementEvolutivoUsage = async (): Promise<boolean> => {
  if (!isSupabaseAvailable()) {
    console.warn('Supabase not available, cannot track evolutivo usage');
    return false;
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('No authenticated user, cannot track evolutivo usage');
      return false;
    }

    const currentMonth = new Date().toISOString().slice(0, 7); // 'YYYY-MM'

    // Try to find existing usage record for current month
    const { data: existingUsage, error: selectError } = await supabase!
      .from('usage_tracking')
      .select('*')
      .eq('user_id', user.id)
      .eq('month_year', currentMonth)
      .maybeSingle();

    if (selectError) {
      console.error('Error checking usage tracking:', selectError);
      return false;
    }

    if (existingUsage) {
      const currentCount = existingUsage.evolutivo_analyses_generated || 0;
      const newCount = currentCount + 1;

      const { error: updateError } = await supabase!
        .from('usage_tracking')
        .update({
          evolutivo_analyses_generated: newCount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingUsage.id);

      if (updateError) {
        console.error('Error updating evolutivo usage:', updateError);
        if (updateError.message?.includes('evolutivo_analyses_generated')) {
          console.error('La columna evolutivo_analyses_generated no existe. Ejecuta la migración 20260105_add_evolutivo_tracking.sql');
        }
        return false;
      }

      return true;
    } else {
      const { error: insertError } = await supabase!
        .from('usage_tracking')
        .insert({
          user_id: user.id,
          month_year: currentMonth,
          tests_taken: 0,
          evolutivo_analyses_generated: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error('Error creating usage tracking record:', insertError);
        if (insertError.message?.includes('evolutivo_analyses_generated')) {
          console.error('La columna evolutivo_analyses_generated no existe. Ejecuta la migración 20260105_add_evolutivo_tracking.sql');
        }
        return false;
      }

      return true;
    }
  } catch (err) {
    console.error('Failed to increment evolutivo usage:', err);
    return false;
  }
};
